import React, {useState} from 'react';
import {Stack, Box, Title, Text, Group, Button, Anchor, Textarea} from '@mantine/core';
import {ProfileBio} from './ProfileBio';
import {SocialLinks} from './SocialLinks';
import {SocialLinksEditor} from './SocialLinksEditor';
import {SharedTeams} from './SharedTeams';
import {useAvatarImage} from '@/hooks/useAvatarImage';
import {Avatar} from '@/components/Avatar';
import {AvatarUpload} from '@/components/AvatarUpload';
import {useModule} from '@/hooks/useModule';
import {useAction} from '@/store/kysely_module';
import {useMutation} from '@tanstack/react-query';
import {notifications} from '@mantine/notifications';
import {useCurrentUser} from '../../hooks/useCurrentUser';
import {SocialLink} from '@/types/social_links';
import {stringifySocialLinks, buildSocialUrl} from '@/utils/social_links_helpers';

type ProfileSidebarProps = {
    userId: string;
    userName: string;
    userHandle: string;
    profilePictureFileId?: string | null;
    updatedAt?: string | null;
    bio?: string;
    socialLinks?: SocialLink[];
    sharedTeams?: Array<{
        id: string;
        name: string;
    }>;
    isEditMode?: boolean;
    onUpdate?: () => void;
};


export const ProfileSidebar = (props: ProfileSidebarProps) => {
    const currentUser = useCurrentUser();
    const filesModule = useModule('MSFiles');
    const userModule = useModule('User');
    const addFileToWorkspaceAction = useAction('addFileToWorkspace');
    const updateObjectAttributeAction = useAction('updateObjectAttribute');

    const {data: profilePictureUrl} = useAvatarImage(props.userId, props.profilePictureFileId || null, props.updatedAt || null);

    const [isEditingAvatar, setIsEditingAvatar] = useState(false);
    const [isEditingBio, setIsEditingBio] = useState(false);
    const [isEditingSocial, setIsEditingSocial] = useState(false);

    const [uploadedFileId, setUploadedFileId] = useState<string | null>(null);
    const [newUploadedBlobUrl, setNewUploadedBlobUrl] = useState<string | null>(null);
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    const [bioEdit, setBioEdit] = useState(props.bio || '');
    const [socialLinksEdit, setSocialLinksEdit] = useState<SocialLink[]>(props.socialLinks || []);

    const currentAvatarSrc = newUploadedBlobUrl || profilePictureUrl;

    const handleChooseImage = async () => {
        if (!currentUser) return;
        setIsUploadingImage(true);
        try {
            const result = await filesModule.client().chooseFileAndUpload(currentUser.id, {
                shouldUseDirectory: false,
                onUploadStart: (fileName) => {
                    notifications.show({
                        id: 'profile-image-upload',
                        loading: true,
                        title: 'Uploading profile image',
                        message: fileName,
                        autoClose: false,
                    });
                },
                onIsDirectory: () => {},
            });

            notifications.hide('profile-image-upload');

            if (result) {
                const fileInfo = {
                    name: result.name,
                    fullPath: result.fullPath,
                    mimeType: result.mimeType,
                    size: result.size,
                    workspace_id: currentUser.id,
                    hash: result.hash,
                    lastModifiedAt: result.lastModifiedAt || Date.now(),
                };

                const workspaceFile = await addFileToWorkspaceAction(fileInfo, {workspace_id: currentUser.id});
                if (workspaceFile) {
                    setUploadedFileId(workspaceFile.id);
                    const src = await filesModule.client().getFileSrc(workspaceFile);
                    setNewUploadedBlobUrl(src);
                }
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            notifications.show({
                title: 'Error uploading image',
                message: 'Please try again',
                color: 'red',
            });
        } finally {
            setIsUploadingImage(false);
        }
    };

    const saveAvatarMutation = useMutation({
        mutationFn: async () => {
            if (!currentUser || !uploadedFileId) return;
            await userModule.updateProfile({profile_picture_file_id: uploadedFileId});
        },
        onSuccess: async () => {
            if (currentUser) {
                await userModule.refreshUser();
                props.onUpdate?.();
                notifications.show({
                    title: 'Avatar updated',
                    message: 'Your profile picture has been updated',
                    color: 'green',
                });
                setIsEditingAvatar(false);
                setUploadedFileId(null);
                setNewUploadedBlobUrl(null);
            }
        },
        onError: () => {
            notifications.show({
                title: 'Error updating avatar',
                message: 'Please try again',
                color: 'red',
            });
        },
    });

    const saveBioMutation = useMutation({
        mutationFn: async () => {
            if (!currentUser) return;
            await updateObjectAttributeAction({
                parent_id: currentUser.id,
                parent_type: 'User',
                attribute: 'bio',
                value: bioEdit.trim() || null,
            }, {workspace_id: currentUser.id});
        },
        onSuccess: async () => {
            if (currentUser) {
                await userModule.refreshUser();
                props.onUpdate?.();
                notifications.show({
                    title: 'Bio updated',
                    message: 'Your bio has been updated',
                    color: 'green',
                });
                setIsEditingBio(false);
            }
        },
        onError: () => {
            notifications.show({
                title: 'Error updating bio',
                message: 'Please try again',
                color: 'red',
            });
        },
    });

    const saveSocialMutation = useMutation({
        mutationFn: async () => {
            if (!currentUser) return;

            const linksWithFullUrls = socialLinksEdit.map(link => ({
                ...link,
                url: buildSocialUrl(link.url, link.type)
            }));

            await updateObjectAttributeAction({
                parent_id: currentUser.id,
                parent_type: 'User',
                attribute: 'social_links',
                value: linksWithFullUrls.length > 0 ? stringifySocialLinks(linksWithFullUrls) : null,
            }, {workspace_id: currentUser.id});
        },
        onSuccess: async () => {
            if (currentUser) {
                await userModule.refreshUser();
                props.onUpdate?.();
                notifications.show({
                    title: 'Social links updated',
                    message: '',
                    color: 'green',
                });
                setIsEditingSocial(false);
            }
        },
        onError: () => {
            notifications.show({
                title: 'Error updating social links',
                message: 'Please try again',
                color: 'red',
            });
        },
    });

    return (
        <Box
            style={{
                width: '100%',
                color: 'var(--mantine-color-text)',
            }}
        >
            <Box style={{width: '100%', position: 'relative'}}>
                <Group h={150}/>
                <Box
                    style={{
                        position: 'absolute',
                        bottom: -60,
                        left: 20,
                    }}
                >
                    {isEditingAvatar ? (
                        <Box>
                            <AvatarUpload
                                userName={props.userName}
                                currentImageUrl={currentAvatarSrc}
                                size="lg"
                                onImageSelect={handleChooseImage}
                                isUploading={isUploadingImage}
                                disabled={saveAvatarMutation.isPending}
                            />
                            <Group gap="xs" mt="xs">
                                <Button
                                    size="xs"
                                    onClick={() => saveAvatarMutation.mutate()}
                                    loading={saveAvatarMutation.isPending}
                                    disabled={!uploadedFileId}
                                >
                                    Save
                                </Button>
                                <Button
                                    size="xs"
                                    variant="default"
                                    onClick={() => {
                                        setIsEditingAvatar(false);
                                        setUploadedFileId(null);
                                        setNewUploadedBlobUrl(null);
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Group>
                        </Box>
                    ) : (
                        <Box style={{position: 'relative'}}>
                            <Avatar
                                src={profilePictureUrl}
                                userName={props.userName}
                                size="lg"
                                style={{
                                    border: '4px solid var(--mantine-color-body)',
                                }}
                            />
                            {props.isEditMode && (
                                <Anchor
                                    size="xs"
                                    onClick={() => setIsEditingAvatar(true)}
                                    style={{
                                        position: 'absolute',
                                        bottom: 0,
                                        right: 0,
                                        backgroundColor: 'var(--mantine-color-body)',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                    }}
                                >
                                    Edit
                                </Anchor>
                            )}
                        </Box>
                    )}
                </Box>
            </Box>

            <Stack gap="md" p="md" pt={70}>
                <Box>
                    <Title order={2} size="h3">
                        {props.userName}
                    </Title>
                    <Text c="blue" size="sm">
                        @{props.userHandle}
                    </Text>
                </Box>

                <Box>
                    {props.isEditMode && !props.bio && !isEditingBio && (
                        <Anchor size="sm" onClick={() => setIsEditingBio(true)}>
                            Add bio
                        </Anchor>
                    )}
                    {isEditingBio ? (
                        <Box>
                            <Text size="sm" mb="xs" fw={500}>Bio</Text>
                            <Textarea
                                size='sm'
                                placeholder="Tell us about yourself..."
                                value={bioEdit}
                                onChange={e => setBioEdit(e.target.value)}
                                rows={3}
                                maxLength={500}
                            />
                            <Text size="xs" c="dimmed" mt={4} mb="xs">
                                {bioEdit.length}/500 characters
                            </Text>
                            <Group gap="xs">
                                <Button
                                    size="xs"
                                    onClick={() => saveBioMutation.mutate()}
                                    loading={saveBioMutation.isPending}
                                >
                                    Save
                                </Button>
                                <Button
                                    size="xs"
                                    variant="default"
                                    onClick={() => {
                                        setIsEditingBio(false);
                                        setBioEdit(props.bio || '');
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Group>
                        </Box>
                    ) : (
                        props.bio && (
                            <Box style={{position: 'relative'}}>
                                {props.isEditMode && (
                                    <Anchor
                                        size="xs"
                                        onClick={() => setIsEditingBio(true)}
                                        style={{position: 'absolute', top: 0, right: 0}}
                                    >
                                        Edit
                                    </Anchor>
                                )}
                                <ProfileBio bio={props.bio} />
                            </Box>
                        )
                    )}
                </Box>

                <Box>
                    {props.isEditMode && (!props.socialLinks || props.socialLinks.length === 0) && !isEditingSocial && (
                        <Anchor size="sm" onClick={() => setIsEditingSocial(true)}>
                            Add social links
                        </Anchor>
                    )}
                    {isEditingSocial ? (
                        <Box>
                            <Text size="sm" mb="xs" fw={500}>Social Links</Text>
                            <SocialLinksEditor
                                links={socialLinksEdit}
                                onChange={setSocialLinksEdit}
                            />
                            <Group gap="xs" mt="xs">
                                <Button
                                    size="xs"
                                    onClick={() => saveSocialMutation.mutate()}
                                    loading={saveSocialMutation.isPending}
                                >
                                    Save
                                </Button>
                                <Button
                                    size="xs"
                                    variant="default"
                                    onClick={() => {
                                        setIsEditingSocial(false);
                                        setSocialLinksEdit(props.socialLinks || []);
                                    }}
                                >
                                    Cancel
                                </Button>
                            </Group>
                        </Box>
                    ) : (
                        props.socialLinks && props.socialLinks.length > 0 && (
                            <Box style={{position: 'relative'}}>
                                {props.isEditMode && (
                                    <Anchor
                                        size="xs"
                                        onClick={() => setIsEditingSocial(true)}
                                        style={{position: 'absolute', top: 0, right: 0}}
                                    >
                                        Edit
                                    </Anchor>
                                )}
                                <SocialLinks
                                    links={props.socialLinks}
                                    isEditMode={props.isEditMode}
                                />
                            </Box>
                        )
                    )}
                </Box>

                {props.sharedTeams && props.sharedTeams.length > 0 && (
                    <SharedTeams teams={props.sharedTeams} />
                )}
            </Stack>
        </Box>
    );
};
